import React, { useMemo } from "react";
import { Avatar } from "@fluentui/react-components";
import { Pencil, Trash2, Share2, Mail, Phone } from "lucide-react";
import HoverCard from "../hoverCard/HoverCard";
import useNavigation from "../../utils/useNavigation";
import { entityTypeToParentType, formatPhoneForTelHref, formatPhoneNumberForDisplay } from "../../helpers/utils";
import styles from "./ContactsPanel.module.scss";

const originBadge = (contact) => {
  if (contact.isDirect) return { label: "Direct", name: null, className: "contact-direct" };
  return { label: "From", name: contact.owner?.name, className: "contact-inherited" };
};

const displayName = (contact) => contact.name || contact.email || contact.phone || "";

const ContactCardList = ({ contacts, parentType, onEdit, onDelete }) => {
  const { navigateTo } = useNavigation();

  const sortedContacts = useMemo(
    () =>
      [...contacts].sort((a, b) => {
        if (a.isDirect !== b.isDirect) return a.isDirect ? -1 : 1;
        return displayName(a).localeCompare(displayName(b));
      }),
    [contacts]
  );

  const goToOwner = (contact) => {
    if (!contact.owner) return;
    navigateTo(entityTypeToParentType(contact.owner.type), contact.owner.id, contact.owner.name);
  };

  return (
    <div className={styles.cardGrid}>
      {sortedContacts.map((contact) => {
        const badge = originBadge(contact);
        return (
          <div key={contact.id} className={styles.contactCard}>
            <div className="d-flex justify-content-between align-items-start">
              <div className="d-flex align-items-center">
                <HoverCard
                  HoverCardTrigger={
                    <Avatar color="colorful" size={40} name={contact.name || contact.email || contact.phone} />
                  }
                  AvatarId={contact.id}
                  AvatarName={contact.name}
                  AvatarEmail={contact.email}
                  AvatarPhone={contact.phone}
                  AvatarIsExternal={true}
                  CanSendMessage={false}
                />
                <div className="ms-12">
                  <p className={`mb-0 ${styles.contactName}`}>
                    {contact.name || contact.email || contact.phone || "Unnamed contact"}
                  </p>
                </div>
              </div>
              {contact.isShared && (
                <span title="Shared - can be attached elsewhere in this hierarchy" className={styles.sharedBadge}>
                  <Share2 size={13} />
                </span>
              )}
            </div>

            {(contact.email || contact.phone) && (
              <div className={styles.contactMeta}>
                {contact.email && (
                  <a href={`mailto:${contact.email}`} className={styles.metaLink} onClick={(e) => e.stopPropagation()}>
                    <Mail size={13} />
                    <span>{contact.email}</span>
                  </a>
                )}
                {contact.phone && (
                  <a
                    href={formatPhoneForTelHref(contact.phone)}
                    className={styles.metaLink}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Phone size={13} />
                    <span>{formatPhoneNumberForDisplay(contact.phone)}</span>
                  </a>
                )}
              </div>
            )}

            {contact.notes && <p className={styles.notes}>{contact.notes}</p>}

            <div className={styles.contactCardFooter}>
              <p
                className={`detailBadge mb-0 ${badge.className} ${!contact.isDirect ? "cursor-pointer" : ""}`}
                onClick={() => !contact.isDirect && goToOwner(contact)}
                title={!contact.isDirect ? `Go to ${badge.name || "owner"}` : undefined}
              >
                <span className={styles.badgeLabel}>{badge.label}</span>
                {badge.name && <span className={styles.badgeName}>{badge.name}</span>}
              </p>

              <div className="d-flex align-items-center gap-1">
                {contact.isDirect && (
                  <>
                    <button type="button" className={styles.iconBtn} title="Edit" onClick={() => onEdit(contact)}>
                      <Pencil size={14} />
                    </button>
                    <button
                      type="button"
                      className={styles.iconBtn}
                      title="Delete"
                      onClick={() => onDelete(contact)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ContactCardList;
